import classNames from 'classnames';
import EmployeeCheckList from 'components/domain/member/EmployeeCheckList/EmployeeCheckList';
import HeaderDialog from 'components/foundation/dialogs/HeaderDialog/HeaderDialog';
import { useOpennable } from 'hooks';
import { useTranslation } from 'react-i18next';
import IMemberPermissionType from 'types/MemberPermissionType';
import MultiPicker from '../../../foundation/pickers/MultiPicker/MultiPicker';
import EmployeePicker from '../EmployeePicker/EmployeePicker';
import './EmployeeMultiPicker.scss';

export interface IEmployeeMultiPickerProps {
  readonly values: string[];
  permissions?: IMemberPermissionType;
  blacklistedIds?: string[];
  filterAlreadySelectedByBlacklist: boolean;
  onChange: (values: string[]) => void;
  error?: boolean;
  placeholder?: string;
  className?: string | object;
  includeClockedIn?: boolean;
}

const EmployeeMultiPicker = ({
  permissions,
  onChange,
  blacklistedIds,
  filterAlreadySelectedByBlacklist,
  values,
  error,
  placeholder,
  className,
  includeClockedIn,
}: IEmployeeMultiPickerProps) => {
  const [t] = useTranslation();

  const checkListDialogDetails = useOpennable();

  function onEmployeeCheckListSubmit(memberIds: string[]) {
    checkListDialogDetails.close();
    onChange(memberIds);
  }

  function handleOnChange(newValues: string[]) {
    if (values.length > 1) {
      onChange(newValues.filter((value) => value !== null));
    } else {
      onChange(newValues);
    }
  }

  function renderItem(value: string | null, index: number) {
    const onMemberSelect = (id: string | null) => {
      if (index === 0 || index < values.length) {
        if (id !== null) {
          const newValues = [...values];
          newValues[index] = id;
          handleOnChange(newValues);
        } else {
          handleOnChange(values.filter((v, i) => i !== index));
        }
      }
    };

    return (
      <EmployeePicker
        className={index !== 0 ? 'mt-4' : ''}
        blacklistedIds={blacklistedIds ? values.concat(blacklistedIds) : values}
        permissions={permissions}
        key={value ? value : 'null'}
        error={error}
        value={value}
        onSelect={onMemberSelect}
        placeholder={placeholder}
        includeClockedIn={includeClockedIn}
      />
    );
  }

  const filteredValues =
    filterAlreadySelectedByBlacklist && blacklistedIds
      ? values.filter((value) => !blacklistedIds.some((blacklistId) => blacklistId === value))
      : values;

  const classes = classNames('employee-multi-picker', className);

  return (
    <div className={classes}>
      <HeaderDialog
        title={t('Select Employees')}
        isOpen={checkListDialogDetails.isOpen}
        onClose={checkListDialogDetails.close}
      >
        <EmployeeCheckList
          checkedItems={filteredValues}
          enableGroups={true}
          enableSearch={true}
          onSubmit={onEmployeeCheckListSubmit}
          permissions={permissions}
          blacklistedIds={blacklistedIds}
          includeClockedIn={includeClockedIn}
        />
      </HeaderDialog>
      <MultiPicker
        onAddClick={checkListDialogDetails.open}
        limit={3}
        renderItem={renderItem}
        // Null list item so there's an empty employee picker as a default
        values={filteredValues && filteredValues.length ? filteredValues : [null]}
      />
    </div>
  );
};

EmployeeMultiPicker.defaultProps = {
  filterAlreadySelectedByBlacklist: true,
};

export default EmployeeMultiPicker;
